import React, { useMemo, useState } from 'react';

import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import CakeOutlinedIcon from '@mui/icons-material/CakeOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import SmartphoneOutlinedIcon from '@mui/icons-material/SmartphoneOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import DOMPurify from 'dompurify';

import { useSetupPortalState } from './useSetupPortalState';
import { setupPortalItemUrl } from '../api/setupPortalApi';
import { onboardColors } from '../theme/onboardTokens';

import type { SvgIconComponent } from '@mui/icons-material';

interface Props {
  token: string;
  expired?: boolean;
}

const FONT = 'Source Sans Pro, sans-serif';
const PAGE_BG = onboardColors.surface.page;
const NEUTRAL_600 = onboardColors.neutral.textHeading;
const PRIMARY = onboardColors.brand.primary;
const TEAL_75 = onboardColors.brandTint.teal75;
const NEUTRAL_500 = onboardColors.neutral.textBody;
const VALUE_COLOR = onboardColors.neutral.textStrong;
const CARD_SX = {
  backgroundColor: onboardColors.surface.white,
  borderRadius: '32px',
  padding: { xs: '24px', md: '40px' }
} as const;
// Long emails, addresses and filenames wrap instead of widening the page on phones.
const WRAP_SX = { minWidth: 0, overflowWrap: 'anywhere' } as const;
// Below `md` the icon buttons grow to a 40px touch target.
const ICON_BUTTON_SX = {
  color: onboardColors.neutral.textBody,
  padding: { xs: '10px', md: '5px' }
};

const ROWS_PER_PAGE = 10;

interface IFieldTile {
  key: string;
  label: string;
  value: string;
  icon: SvgIconComponent;
}

// Compose the dynamic candidate fields into the labelled tiles shown in the design.
// The admin chooses which fields to share, so each tile only appears when its
// underlying field(s) were selected.
const buildFieldTiles = (fields: Record<string, unknown>): IFieldTile[] => {
  const has = (key: string) => key in fields;
  const val = (key: string) => String(fields[key] ?? '').trim();
  const tiles: IFieldTile[] = [];

  if (has('first_name') || has('last_name')) {
    const name = [val('first_name'), val('last_name')].filter(Boolean).join(' ');
    tiles.push({ key: 'name', label: 'NAME', value: name, icon: BadgeOutlinedIcon });
  }
  if (has('email'))
    tiles.push({ key: 'email', label: 'EMAIL', value: val('email'), icon: EmailOutlinedIcon });
  if (has('phone')) {
    tiles.push({ key: 'phone', label: 'PHONE', value: val('phone'), icon: LocalPhoneOutlinedIcon });
  }
  if (has('mobile')) {
    tiles.push({
      key: 'mobile',
      label: 'MOBILE',
      value: val('mobile'),
      icon: SmartphoneOutlinedIcon
    });
  }
  if (has('address') || has('city') || has('state') || has('postcode') || has('country')) {
    // The `address` field already covers street/suburb/postcode; append the separately
    // selected state/country (and fall back to city/postcode when no full address) so no
    // selected field's value is dropped.
    const fullAddress = has('address') ? val('address') : '';
    const parts: string[] = [];
    if (fullAddress) parts.push(fullAddress);
    else if (has('city')) parts.push(val('city'));
    if (has('state')) parts.push(val('state'));
    if (!fullAddress && has('postcode')) parts.push(val('postcode'));
    if (has('country')) parts.push(val('country'));
    tiles.push({
      key: 'address',
      label: 'ADDRESS',
      value: parts.filter(Boolean).join(', '),
      icon: PlaceOutlinedIcon
    });
  }
  if (has('date_of_birth')) {
    tiles.push({
      key: 'date_of_birth',
      label: 'DATE OF BIRTH',
      value: val('date_of_birth'),
      icon: CakeOutlinedIcon
    });
  }
  if (has('start_date')) {
    tiles.push({
      key: 'start_date',
      label: 'START DATE',
      value: val('start_date'),
      icon: CalendarTodayOutlinedIcon
    });
  }

  const handled = new Set([
    'first_name',
    'last_name',
    'email',
    'phone',
    'mobile',
    'address',
    'city',
    'state',
    'postcode',
    'country',
    'date_of_birth',
    'start_date'
  ]);
  Object.keys(fields)
    .filter((key) => !handled.has(key))
    .forEach((key) => {
      tiles.push({
        key,
        label: key.replace(/_/g, ' ').toUpperCase(),
        value: val(key),
        icon: DescriptionOutlinedIcon
      });
    });

  return tiles;
};

const CenteredMessage = ({ title, body }: { title: string; body: string }) => (
  <Box sx={{ margin: '48px auto', maxWidth: 560, padding: '24px', textAlign: 'center' }}>
    <Typography sx={{ color: NEUTRAL_600, fontFamily: FONT, fontSize: '24px', fontWeight: 700 }}>
      {title}
    </Typography>
    <Typography sx={{ color: NEUTRAL_500, fontFamily: FONT, marginTop: '12px' }}>{body}</Typography>
  </Box>
);

const FieldTile = ({ tile }: { tile: IFieldTile }) => {
  const Icon = tile.icon;
  return (
    <Stack gap="8px">
      <Stack alignItems="center" direction="row" gap="8px">
        <Icon sx={{ color: PRIMARY, fontSize: '16px' }} />
        <Typography
          sx={{ ...WRAP_SX, color: PRIMARY, fontFamily: FONT, fontSize: '14px', fontWeight: 700 }}
        >
          {tile.label}
        </Typography>
      </Stack>
      <Typography sx={{ ...WRAP_SX, color: VALUE_COLOR, fontFamily: FONT, fontSize: '16px' }}>
        {tile.value || '—'}
      </Typography>
    </Stack>
  );
};

const SetupPortal: React.FC<Props> = ({ token, expired = false }) => {
  const [page, setPage] = useState(0);
  const { data: state, isError } = useSetupPortalState({ token, expired });

  const fieldTiles = useMemo(
    () => buildFieldTiles(state?.candidate_fields ?? {}),
    [state?.candidate_fields]
  );
  const documentItems = useMemo(
    () => (state?.items ?? []).filter((item) => item.download_path),
    [state?.items]
  );

  if (expired || isError) {
    const body = isError
      ? 'This portal link is no longer available.'
      : 'This link has expired or been revoked.';
    return <CenteredMessage body={body} title="Portal unavailable" />;
  }

  if (!state) {
    return <CenteredMessage body="" title="Loading…" />;
  }

  const totalPages = Math.max(1, Math.ceil(documentItems.length / ROWS_PER_PAGE));
  const pageItems = documentItems.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE);

  const downloadAll = () => {
    documentItems.forEach((item) => {
      const anchor = document.createElement('a');
      anchor.href = setupPortalItemUrl(token, item.id, true);
      anchor.target = '_blank';
      anchor.rel = 'noopener';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    });
  };

  return (
    <Box sx={{ backgroundColor: PAGE_BG, paddingBottom: '48px', paddingTop: '8px' }}>
      <Box sx={{ margin: '0 auto', maxWidth: 1016, padding: { xs: '0 16px', md: '0 24px' } }}>
        <Stack gap="6px" sx={{ paddingTop: '40px' }}>
          <Typography
            sx={{
              ...WRAP_SX,
              color: NEUTRAL_600,
              fontFamily: FONT,
              fontSize: { xs: '28px', md: '36px' },
              fontWeight: 700,
              letterSpacing: '-0.72px'
            }}
          >
            Hi, {state.recipient_name ?? 'there'}
          </Typography>
          {state.instruction_html ? (
            <Box
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(state.instruction_html, {
                  ADD_ATTR: ['href'],
                  ADD_TAGS: ['a']
                })
              }}
              sx={{
                ...WRAP_SX,
                color: NEUTRAL_600,
                fontFamily: FONT,
                fontSize: '16px',
                '& img': { height: 'auto', maxWidth: '100%' }
              }}
            />
          ) : (
            <Typography sx={{ color: NEUTRAL_600, fontFamily: FONT, fontSize: '16px' }}>
              Here is our new employee details
            </Typography>
          )}
        </Stack>

        <Stack gap="32px" sx={{ paddingTop: '32px' }}>
          {fieldTiles.length > 0 ? (
            <Box sx={CARD_SX}>
              <Typography
                sx={{
                  color: NEUTRAL_600,
                  fontFamily: FONT,
                  fontSize: '24px',
                  fontWeight: 700,
                  marginBottom: '24px'
                }}
              >
                Employee Details
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gap: '24px',
                  gridTemplateColumns: {
                    xs: 'minmax(0, 1fr)',
                    sm: 'repeat(2, minmax(0, 1fr))',
                    md: 'repeat(4, minmax(0, 1fr))'
                  }
                }}
              >
                {fieldTiles.map((tile) => (
                  <FieldTile key={tile.key} tile={tile} />
                ))}
              </Box>
            </Box>
          ) : null}

          {documentItems.length > 0 ? (
            <Box sx={CARD_SX}>
              <Stack
                alignItems="center"
                direction="row"
                justifyContent="space-between"
                sx={{ flexWrap: 'wrap', gap: '12px', marginBottom: '8px' }}
              >
                <Typography
                  sx={{ color: NEUTRAL_600, fontFamily: FONT, fontSize: '24px', fontWeight: 700 }}
                >
                  Forms &amp; Documents
                </Typography>
                <Box
                  component="button"
                  onClick={downloadAll}
                  sx={{
                    alignItems: 'center',
                    backgroundColor: TEAL_75,
                    border: 0,
                    borderRadius: '6px',
                    color: PRIMARY,
                    cursor: 'pointer',
                    display: 'flex',
                    fontFamily: FONT,
                    fontSize: '14px',
                    fontWeight: 700,
                    gap: '8px',
                    padding: '10px 16px'
                  }}
                >
                  <FileDownloadOutlinedIcon sx={{ fontSize: '18px' }} />
                  Download all files
                </Box>
              </Stack>

              <Box
                sx={{
                  alignItems: 'center',
                  borderBottom: `1px solid ${onboardColors.border.hairline}`,
                  display: 'flex',
                  padding: '12px 0'
                }}
              >
                <Typography
                  sx={{ color: PRIMARY, fontFamily: FONT, fontSize: '14px', fontWeight: 700 }}
                >
                  Name
                </Typography>
              </Box>

              {pageItems.map((item) => (
                <Stack
                  key={item.id}
                  alignItems="center"
                  direction="row"
                  gap="12px"
                  justifyContent="space-between"
                  sx={{
                    borderBottom: `1px solid ${onboardColors.border.hairline}`,
                    padding: { xs: '8px 0', md: '16px 0' }
                  }}
                >
                  <Typography
                    sx={{
                      ...WRAP_SX,
                      color: NEUTRAL_500,
                      fontFamily: FONT,
                      fontSize: '14px',
                      fontWeight: 700
                    }}
                  >
                    {item.label}
                  </Typography>
                  <Stack alignItems="center" direction="row" gap="8px" sx={{ flexShrink: 0 }}>
                    <Tooltip title="Preview">
                      <IconButton
                        component="a"
                        href={setupPortalItemUrl(token, item.id)}
                        rel="noopener noreferrer"
                        size="small"
                        sx={ICON_BUTTON_SX}
                        target="_blank"
                      >
                        <VisibilityIcon sx={{ fontSize: '20px' }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Download">
                      <IconButton
                        component="a"
                        href={setupPortalItemUrl(token, item.id, true)}
                        rel="noopener noreferrer"
                        size="small"
                        sx={ICON_BUTTON_SX}
                        target="_blank"
                      >
                        <FileDownloadOutlinedIcon sx={{ fontSize: '20px' }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
              ))}

              <Stack
                alignItems="center"
                direction="row"
                justifyContent="space-between"
                sx={{ flexWrap: 'wrap', gap: '12px', paddingTop: '20px' }}
              >
                <Stack alignItems="center" direction="row" gap="8px">
                  <Typography
                    sx={{ color: NEUTRAL_500, fontFamily: FONT, fontSize: '14px', fontWeight: 700 }}
                  >
                    Rows per page:
                  </Typography>
                  <Box
                    sx={{
                      border: `1px solid ${onboardColors.border.inputStrong}`,
                      borderRadius: '6px',
                      color: NEUTRAL_500,
                      fontFamily: FONT,
                      fontSize: '14px',
                      fontWeight: 700,
                      padding: '8px 12px'
                    }}
                  >
                    {ROWS_PER_PAGE}
                  </Box>
                </Stack>
                <Stack alignItems="center" direction="row" gap="4px" sx={{ flexWrap: 'wrap' }}>
                  <IconButton
                    disabled={page === 0}
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    size="small"
                    sx={{ color: NEUTRAL_500, padding: { xs: '9px', md: '5px' } }}
                  >
                    <KeyboardArrowLeftIcon sx={{ fontSize: '18px' }} />
                  </IconButton>
                  {Array.from({ length: totalPages }, (_, i) => i).map((p) => (
                    <Box
                      key={p}
                      component="button"
                      onClick={() => setPage(p)}
                      sx={{
                        background: 'none',
                        border: 0,
                        color: p === page ? PRIMARY : onboardColors.neutral.textPlaceholderLight,
                        cursor: 'pointer',
                        fontFamily: FONT,
                        fontSize: '14px',
                        fontWeight: 700,
                        minHeight: { xs: '36px', md: 0 },
                        minWidth: { xs: '36px', md: 0 },
                        padding: '4px 8px'
                      }}
                    >
                      {p + 1}
                    </Box>
                  ))}
                  <IconButton
                    disabled={page >= totalPages - 1}
                    onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                    size="small"
                    sx={{ color: NEUTRAL_500, padding: { xs: '9px', md: '5px' } }}
                  >
                    <KeyboardArrowRightIcon sx={{ fontSize: '18px' }} />
                  </IconButton>
                  <Typography
                    sx={{
                      color: NEUTRAL_500,
                      fontFamily: FONT,
                      fontSize: '14px',
                      fontWeight: 700,
                      marginLeft: '8px'
                    }}
                  >
                    {documentItems.length} Total
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          ) : null}
        </Stack>

        <Stack
          alignItems="center"
          direction="row"
          justifyContent="space-between"
          sx={{ flexWrap: 'wrap', gap: '8px', paddingTop: '32px' }}
        >
          <Stack direction="row" gap="6px">
            <Typography
              component="a"
              href="https://www.applynow.com.au/privacy"
              rel="noopener noreferrer"
              target="_blank"
              sx={{
                color: onboardColors.neutral.textFooter,
                fontFamily: FONT,
                fontSize: '14px',
                textDecoration: 'underline'
              }}
            >
              Privacy Policy
            </Typography>
            <Typography
              sx={{ color: onboardColors.neutral.textFooter, fontFamily: FONT, fontSize: '14px' }}
            >
              |
            </Typography>
            <Typography
              component="a"
              href="https://www.applynow.com.au/terms"
              rel="noopener noreferrer"
              target="_blank"
              sx={{
                color: onboardColors.neutral.textFooter,
                fontFamily: FONT,
                fontSize: '14px',
                textDecoration: 'underline'
              }}
            >
              Terms and Conditions
            </Typography>
          </Stack>
          <Typography
            sx={{ color: onboardColors.neutral.textFooter, fontFamily: FONT, fontSize: '14px' }}
          >
            Powered by{' '}
            <Typography
              component="a"
              href="https://scouttalenthq.com/"
              rel="noopener noreferrer"
              sx={{ color: onboardColors.neutral.textFooter, fontFamily: FONT, fontSize: '14px' }}
              target="_blank"
            >
              Scout Talent
            </Typography>
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
};

export default SetupPortal;
